/**
 * Библиотека и чтение · Фаза 3 окна «книги».
 *
 * Один экран, три вида: полка → обложка → страница. ⛔ Шестого пункта навигации не
 * появилось (их по-прежнему пять, как решили дизайн-ворота): вход — карточкой в кабинете.
 *
 * ⭐ Страница набирается ДО ПЕРЕПОЛНЕНИЯ поля, а не по выдуманному числу слов: сколько
 * влезло — столько и есть страница. Поэтому при смене кегля, повороте и на другом
 * устройстве разбивка своя, а закладка хранит НОМЕР АБЗАЦА и переживает всё это.
 *
 * ⛔ Свайпа нет намеренно: горизонтальный жест у края закрывает окно Mini App
 * (`knowledge/mini-app-surfaces.md`), и человек теряет и страницу, и приложение.
 * Листание — кнопки внизу и узкие поля по краям страницы.
 *
 * ⛔ Витрина ничего не считает: покрытие, проценты, «осталось минут» и разбор каждого
 * слова приезжают от ядра (`core/books.py`). Здесь только раскладка и тексты.
 */

import * as api from "../api";
import { play } from "../audio";
import { state } from "../state";
import { tap } from "../tg";
import {
  MISSING_NOTE, SIZE_PX, coverageLabel, ipaAtWord, leftLabel, pageLabel, pieces,
  posLabel, progressLabel, sentenceLabel, showsLemma, stateLabel, tappable, wordClass,
  type Highlight,
} from "../reader";

/** Сколько абзацев тянем за раз: страница — это 1–4 абзаца, запас на листание вперёд. */
const CHUNK = 60;

interface View {
  shelf: api.BookRow[] | null;
  slug: string | null;
  book: api.BookRow | null;
  page: api.BookPage | null;
  names: Set<string>;
  /** Разбивка на страницы: индексы кусков потока. */
  pages: [number, number][];
  cur: number;
  mode: "shelf" | "cover" | "read";
  busy: boolean;
}

const v: View = { shelf: null, slug: null, book: null, page: null, names: new Set(),
                  pages: [], cur: 0, mode: "shelf", busy: false };

/** Поток кусков текущего куска книги: слово, текст между словами или конец абзаца. */
type Node = { br: true } | { txt: string } | { w: string; key: string; idx: number };
let flow: Node[] = [];
let host: HTMLElement | null = null;

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

const size = (): keyof typeof SIZE_PX => (state.me?.settings.reader_size as string) || "m";
const highlight = (): Highlight =>
  ((state.me?.settings.reader_highlight as Highlight) || "off");

/* ── вход ──────────────────────────────────────────────────────────────────── */

/** Открыть библиотеку (зовётся из кабинета). Полку берём у ядра при каждом заходе. */
export function enterLibrary(): void {
  v.mode = "shelf";
  v.slug = null;
  void loadShelf();
}

async function loadShelf(): Promise<void> {
  try {
    v.shelf = (await api.books()).books;
  } catch {
    v.shelf = [];
  }
  draw();
}

export function renderLibrary(el: HTMLElement): void {
  host = el;
  draw();
}

/* ── полка ─────────────────────────────────────────────────────────────────── */

function shelfHtml(): string {
  if (v.shelf === null) return '<div class="stub"><p>Книги едут…</p></div>';
  if (!v.shelf.length) {
    return '<div class="stub"><p>Библиотека пока пуста.</p></div>';
  }
  const cards = v.shelf.map((b, i) =>
    `<button class="bk" type="button" data-open="${esc(b.slug)}">` +
      `<span class="cov c${(i % 5) + 1}"><span class="ct">${esc(b.title)}</span>` +
      `<span class="crule"></span><span class="ca">${esc(b.author || "")}</span></span>` +
      '<span class="bkmeta">' +
        `<b>${esc(b.title)}</b>` +
        `<span class="sub">${b.words_total.toLocaleString("ru-RU")} слов · ${sentenceLabel(b.median_sentence)}</span>` +
        `<span class="sub2">${progressLabel(b)}</span>` +
        (b.started ? `<span class="bar"><i style="width:${b.percent}%"></i></span>` : "") +
      "</span>" +
    "</button>").join("");
  return (
    '<div class="libhd"><h2>Библиотека</h2>' +
      '<p class="sub">Сверху книги, где фраза короче — с них легче начать. ' +
      "Срок авторского права у всех истёк: читать законно.</p></div>" +
    `<div class="books">${cards}</div>` +
    '<p class="footnote">Тексты — проект «Гутенберг». Тап по слову в книге открывает ' +
    "перевод, транскрипцию и звук — тот же словарь, что на занятии.</p>"
  );
}

/* ── обложка ───────────────────────────────────────────────────────────────── */

function coverHtml(): string {
  const b = v.book;
  if (!b) return '<div class="stub"><p>Книга едет…</p></div>';
  const facts = [
    ["Слов в книге", b.words_total.toLocaleString("ru-RU")],
    ["Длина фразы", b.median_sentence ? `${b.median_sentence} слов` : "—"],
    ["Словарь объяснит", b.coverage === null ? "—"
      : `${(b.coverage * 100).toFixed(1).replace(".", ",")} %`],
    ["Вы прочитали", progressLabel(b)],
  ];
  return (
    '<button class="linkback" type="button" data-back="shelf">‹ Библиотека</button>' +
    `<div class="bigcov"><div><div class="ct">${esc(b.title)}</div><div class="crule"></div>` +
      `<div class="ca">${esc(b.author || "")}</div></div></div>` +
    '<div class="facts">' +
      facts.map(([k, val]) => `<div class="fact"><span>${k}</span><b>${val}</b></div>`).join("") +
    "</div>" +
    `<p class="hsub">${esc(leftLabel(b))}</p>` +
    `<button class="cta wide" type="button" data-read="1"><div class="big">` +
      `${b.started ? "Продолжить" : "Начать читать"}</div>` +
      `<div class="small">${esc(coverageLabel(b.coverage))}</div></button>`
  );
}

/* ── чтение ────────────────────────────────────────────────────────────────── */

function readHtml(): string {
  const b = v.book;
  return (
    '<div class="readerhd">' +
      '<button class="iconbtn" type="button" data-back="cover" aria-label="К обложке">‹</button>' +
      `<div class="ttl">${esc(b?.title || "")}</div>` +
      '<button class="iconbtn" type="button" id="aa" aria-label="Размер шрифта">Aa</button>' +
    "</div>" +
    '<div class="paper">' +
      `<div class="pagearea hl-${highlight()}" id="page" style="font-size:${SIZE_PX[size()]}px"></div>` +
      '<div class="tapzone l" id="tz-l" role="button" aria-label="Предыдущая страница"></div>' +
      '<div class="tapzone r" id="tz-r" role="button" aria-label="Следующая страница"></div>' +
    "</div>" +
    '<div class="foot">' +
      '<button class="navb" type="button" id="prev" aria-label="Назад">‹</button>' +
      '<div class="track"><div class="fill" id="fill"></div></div>' +
      '<span class="pgn" id="pgn">1 / 1</span>' +
      '<button class="navb" type="button" id="next" aria-label="Вперёд">›</button>' +
    "</div>" +
    '<div class="scrim" id="scrim"></div>' +
    '<div class="sheet" id="sheet" role="dialog" aria-label="Слово"></div>'
  );
}

/** Собрать поток кусков из абзацев: слово · текст между словами · конец абзаца. */
function buildFlow(page: api.BookPage): void {
  flow = [];
  page.paragraphs.forEach((para, i) => {
    if (i) flow.push({ br: true });
    for (const p of pieces(para)) {
      if (p.word) flow.push({ w: p.text, key: p.key!, idx: page.start + i });
      else flow.push({ txt: p.text });
    }
  });
}

function makeWord(node: { w: string; key: string }): HTMLElement {
  const w = v.page?.words[node.key];
  // ⚠ Не <button>: кнопка для браузера не текст, вокруг неё разрешён перенос строки, и
  // страница рвётся перед знаком препинания («a book» / «,” thought») — урок прототипа.
  const el = document.createElement("span");
  el.textContent = node.w;
  if (!tappable(node.key, v.names)) {
    el.className = "nw";                       // имя собственное или буква — не тапается
    return el;
  }
  el.className = wordClass(w, highlight());
  el.setAttribute("role", "button");
  el.tabIndex = 0;
  el.dataset.key = node.key;
  return el;
}

/** Разбивка «пока влезает»: набираем в невидимую копию поля, пока не переполнилось. */
function paginate(): void {
  const page = host?.querySelector<HTMLElement>("#page");
  if (!page) return;
  const st = getComputedStyle(page);
  // ⚠ Мерная копия обязана быть ТАКОЙ ЖЕ, как страница: не «шрифт и ширина», а тот же
  // класс и то же место в дереве. Иначе к её абзацам не применяются отступы и красная
  // строка из CSS (`.pagearea p`), высота считается по чужим метрикам, и внизу страницы
  // остаётся пустая полоса — дефект, пойманный глазами на живом стенде.
  const probe = page.cloneNode(false) as HTMLElement;
  probe.id = "";
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.left = "-9999px";
  probe.style.top = "0";
  probe.style.height = "auto";
  probe.style.overflow = "visible";
  probe.style.width = `${page.clientWidth}px`;
  probe.style.fontSize = st.fontSize;
  page.parentElement?.appendChild(probe);
  const limit = page.clientHeight - parseFloat(st.paddingTop) - parseFloat(st.paddingBottom);

  v.pages = [];
  let start = 0;
  let p = document.createElement("p");
  probe.appendChild(p);
  for (let i = 0; i < flow.length; i++) {
    const n = flow[i];
    if ("br" in n) { p = document.createElement("p"); probe.appendChild(p); }
    else if ("txt" in n) p.appendChild(document.createTextNode(n.txt));
    else p.appendChild(makeWord(n));
    if (probe.scrollHeight - parseFloat(st.paddingTop) - parseFloat(st.paddingBottom) > limit
        && i > start) {
      v.pages.push([start, i]);
      start = i;
      probe.innerHTML = "";
      p = document.createElement("p");
      probe.appendChild(p);
    }
  }
  if (start < flow.length) v.pages.push([start, flow.length]);
  probe.remove();
  if (!v.pages.length) v.pages = [[0, flow.length]];
  v.cur = Math.min(v.cur, v.pages.length - 1);
  paint();
}

/** Нарисовать текущую страницу. */
function paint(): void {
  const page = host?.querySelector<HTMLElement>("#page");
  if (!page || !v.pages.length) return;
  page.innerHTML = "";
  const [from, to] = v.pages[v.cur];
  let p = document.createElement("p");
  page.appendChild(p);
  for (let i = from; i < to; i++) {
    const n = flow[i];
    if ("br" in n) { p = document.createElement("p"); page.appendChild(p); }
    else if ("txt" in n) p.appendChild(document.createTextNode(n.txt));
    else p.appendChild(makeWord(n));
  }
  const label = host?.querySelector<HTMLElement>("#pgn");
  if (label) label.textContent = pageLabel(v.cur, v.pages.length);
  const fill = host?.querySelector<HTMLElement>("#fill");
  if (fill) fill.style.width = `${((v.cur + 1) / v.pages.length) * 100}%`;
  const prev = host?.querySelector<HTMLButtonElement>("#prev");
  const next = host?.querySelector<HTMLButtonElement>("#next");
  // ⚠ Кнопка гаснет только на КРАЮ КНИГИ, а не на краю загруженного куска: иначе на
  // последней странице куска «вперёд» мертва и книга обрывается на середине — дефект,
  // пойманный живым стендом («Оз» вставал на 15-й странице из 1 139 абзацев).
  const tail = v.page ? v.page.start + v.page.count < v.page.total : false;
  const head = v.page ? v.page.start > 0 : false;
  if (prev) prev.disabled = v.cur === 0 && !head;
  if (next) next.disabled = v.cur === v.pages.length - 1 && !tail;
  saveSoon();
}

/** Закладка: сохраняем НОМЕР АБЗАЦА и не чаще раза в пару секунд. */
let saveTimer: number | undefined;
function saveSoon(): void {
  if (!v.slug || !v.pages.length) return;
  const [from] = v.pages[v.cur];
  let idx = v.page?.start ?? 0;
  for (let i = 0; i <= from && i < flow.length; i++) {
    const n = flow[i];
    if ("w" in n) idx = n.idx;
  }
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    const slug = v.slug;
    if (slug) void api.setBookPosition(slug, idx).catch(() => undefined);
  }, 1200);
}

function turn(step: number): void {
  const n = v.cur + step;
  if (n < 0 || n >= v.pages.length) { void more(step); return; }
  v.cur = n;
  closeSheet();
  paint();
}

/** Кончился загруженный кусок — тянем следующий (или предыдущий) у ядра. */
async function more(step: number): Promise<void> {
  if (!v.page || !v.slug || v.busy) return;
  const nextStart = step > 0 ? v.page.start + v.page.count : v.page.start - CHUNK;
  if (step > 0 && v.page.start + v.page.count >= v.page.total) return;
  if (step < 0 && v.page.start <= 0) return;
  await openText(v.slug, Math.max(0, nextStart), step > 0 ? "first" : "last");
}

async function openText(slug: string, start: number, at: "first" | "last"): Promise<void> {
  v.busy = true;
  try {
    v.page = await api.bookText(slug, start, CHUNK);
    v.names = new Set(v.page.names);
    buildFlow(v.page);
    v.cur = 0;
    draw();
    if (at === "last") { v.cur = Math.max(0, v.pages.length - 1); paint(); }
  } catch {
    /* сеть мигнула — оставляем то, что уже на экране */
  } finally {
    v.busy = false;
  }
}

/* ── карточка слова ────────────────────────────────────────────────────────── */

function closeSheet(): void {
  host?.querySelector("#sheet")?.classList.remove("show");
  host?.querySelector("#scrim")?.classList.remove("show");
  host?.querySelector(".w.on")?.classList.remove("on");
}

function openWord(el: HTMLElement): void {
  const key = el.dataset.key;
  const sheet = host?.querySelector<HTMLElement>("#sheet");
  const scrim = host?.querySelector<HTMLElement>("#scrim");
  if (!key || !sheet || !scrim) return;
  const w = v.page?.words[key];
  host?.querySelector(".w.on")?.classList.remove("on");
  el.classList.add("on");

  let html = '<div class="grip"></div>';
  if (!w || !w.known) {
    html += `<div class="wline"><span class="wd">${esc(el.textContent || "")}</span></div>` +
            '<div class="state miss">нет в словаре</div>' +
            `<div class="missnote">${MISSING_NOTE}</div>` +
            '<div class="acts"><button class="act" type="button" data-close>Понятно</button></div>';
  } else {
    const st = stateLabel(w);
    html += `<div class="wline"><span class="wd">${esc(el.textContent || "")}</span>` +
            (ipaAtWord(w) ? `<span class="ipa">${esc(w.ipa || "")}</span>` : "") + "</div>" +
            `<div class="tr">${esc(w.translation || "")}</div>` +
            (posLabel(w.part_of_speech) ? `<div class="pos">${posLabel(w.part_of_speech)}</div>` : "") +
            (showsLemma(w)
              ? `<div class="lemma">начальная форма — <b>${esc(w.lemma || "")}</b>` +
                (w.ipa ? ` <span class="ipa">${esc(w.ipa)}</span>` : "") + "</div>"
              : "") +
            (w.note ? `<div class="note">${esc(w.note)}</div>` : "") +
            `<div class="state ${st.kind}">${st.text}</div>` +
            '<div class="acts">' +
              (w.lexicon_id
                ? `<button class="act" type="button" data-say="${w.lexicon_id}">▶ Послушать</button>`
                : "") +
              '<button class="act" type="button" data-close>Закрыть</button>' +
            "</div>";
  }
  sheet.innerHTML = html;
  sheet.classList.add("show");
  scrim.classList.add("show");
}

/* ── сборка экрана ─────────────────────────────────────────────────────────── */

function draw(): void {
  if (!host) return;
  const body = v.mode === "shelf" ? shelfHtml() : v.mode === "cover" ? coverHtml() : readHtml();
  host.innerHTML = `<div class="library ${v.mode}">${body}</div>`;
  document.body.classList.toggle("reading", v.mode === "read");
  if (v.mode === "read") {
    // Шрифт книги может доехать позже первого кадра — тогда разбивка «пока влезает»
    // посчиталась бы по чужим метрикам. Пересчитываем, когда шрифты готовы.
    paginate();
    void document.fonts?.ready.then(() => { if (v.mode === "read") paginate(); });
  }
  bind();
}

let bound = false;
function bind(): void {
  if (!host || bound) return;
  bound = true;
  host.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const open = t.closest<HTMLElement>("[data-open]");
    if (open) { tap(open); void openBook(open.dataset.open!); return; }
    const back = t.closest<HTMLElement>("[data-back]");
    if (back) {
      tap(back);
      v.mode = back.dataset.back as View["mode"];
      if (v.mode === "shelf") void loadShelf(); else draw();
      return;
    }
    if (t.closest("[data-read]")) { void startReading(); return; }
    if (t.closest("#next")) { turn(1); return; }
    if (t.closest("#prev")) { turn(-1); return; }
    if (t.closest("#tz-r")) { turn(1); return; }
    if (t.closest("#tz-l")) { turn(-1); return; }
    if (t.closest("#aa")) { cycleSize(); return; }
    if (t.closest("#scrim") || t.closest("[data-close]")) { closeSheet(); return; }
    const say = t.closest<HTMLElement>("[data-say]");
    if (say) { void play(`word:${say.dataset.say}`); return; }
    const word = t.closest<HTMLElement>(".w");
    if (word) { tap(word); openWord(word); }
  });
  host.addEventListener("keydown", (e) => {
    const k = (e as KeyboardEvent).key;
    if (k !== "Enter" && k !== " ") return;
    const word = (e.target as HTMLElement).closest<HTMLElement>(".w");
    if (!word) return;
    e.preventDefault();
    openWord(word);
  });
}

async function openBook(slug: string): Promise<void> {
  v.slug = slug;
  v.mode = "cover";
  v.book = v.shelf?.find((b) => b.slug === slug) ?? null;
  draw();
  try {
    v.book = await api.book(slug);
    draw();
  } catch { /* оставляем то, что уже знаем с полки */ }
}

async function startReading(): Promise<void> {
  if (!v.slug) return;
  v.mode = "read";
  // ⭐ Не начинал — открываем с первого абзаца ТЕКСТА (титул и оглавление пролистываются
  // сами); начинал — с его закладки, даже если она раньше.
  const b = v.book;
  const from = b?.started ? Math.max(0, b.idx) : Math.max(0, b?.start_idx ?? 0);
  await openText(v.slug, from, "first");
}

/** Кегль по кругу S → M → L. Настройка пер-юзерная, живёт в ядре. */
function cycleSize(): void {
  const order: ("s" | "m" | "l")[] = ["s", "m", "l"];
  const next = order[(order.indexOf(size() as "s") + 1) % order.length];
  if (state.me) state.me.settings.reader_size = next;
  const page = host?.querySelector<HTMLElement>("#page");
  if (page) page.style.fontSize = `${SIZE_PX[next]}px`;
  paginate();
  void api.patchSettings({ reader_size: next }).catch(() => undefined);
}

// ⚠ Экран поменял размер (поворот, клавиатура, окно ПК) — страница набрана по СТАРОМУ
// полю и либо обрезана, либо полупустая. Пересобираем разбивку; ⛔ но только в чтении.
let resizeTimer: number | undefined;
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    if (v.mode !== "read") return;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => { if (v.mode === "read") paginate(); }, 180);
  });
}

/** Уйти из чтения (зовётся роутером): гасим класс, которым спрятана навигация. */
export function leaveLibrary(): void {
  document.body.classList.remove("reading");
  closeSheet();
}
